import { type FC, useMemo } from 'react';

import type { AuthenticationBanners } from '@/api/cms/authenticationBanners';
import { ImageComponent } from '@/components/Image';
import { Skeleton } from '@/components/Skeleton';
import { getCustomOffer } from '@/hooks/customOffer';
import { useIsRegModalFlow } from '@/modules/promo';
import {
  useCMSAuthenticationBanner,
  useRegModalTheme,
} from '@/modules/registration';
import { useIsSports } from '@/modules/sports/hooks';
import { useGetCommonStore } from '@/store';
import { ScRegistrationBanner } from '../styled';

export const RegistrationBanner = () => {
  const useCommonStore = useGetCommonStore();

  const regBannerStore = useCommonStore.use.regBanner();
  const { styleScheme } = useRegModalTheme();
  const { banners, isLoading } = useCMSAuthenticationBanner();
  const regModal = useIsRegModalFlow();
  const isSports = useIsSports();
  const useStoreValues = regModal || isSports || regBannerStore?.isSportsPromo;

  if (isLoading || !banners) {
    return (
      <Skeleton.Container
        borderRadius={16}
        flexDirection="column"
        flexGrow={1}
        gap={24}
        marginBottom={32}
        overflowY="auto"
      >
        <Skeleton.Rect bgColorVariant="light" height={106} />
      </Skeleton.Container>
    );
  }

  const regBannerBg =
    (useStoreValues && regBannerStore?.background) ||
    banners.registrationV2?.background?.url;

  const regBannerCharacters =
    (useStoreValues && regBannerStore?.character) ||
    banners.registrationV2?.characters?.url;

  return (
    <ScRegistrationBanner styleScheme={styleScheme}>
      {regBannerBg && (
        <ImageComponent
          alt="characters"
          className="bg"
          fill
          priority
          src={regBannerBg}
        />
      )}

      <div className="offer-container">
        <RegOffer banners={banners} />
      </div>
      {regBannerCharacters && (
        <ImageComponent
          alt="characters"
          className="characters"
          height={104}
          priority
          src={regBannerCharacters}
          width={104}
        />
      )}
    </ScRegistrationBanner>
  );
};

const RegOffer: FC<{
  banners: AuthenticationBanners | null;
}> = ({ banners }) => {
  const customOffer = getCustomOffer();
  const useCommonStore = useGetCommonStore();

  const availableCrossSell = useCommonStore.use.availableCrossSell();
  const regBannerStore = useCommonStore.use.regBanner();
  const regModal = useIsRegModalFlow();
  const isSports = useIsSports();

  const [cmsTitle, cmsOffer1, cmsOffer2] = (() => {
    const offer1 = availableCrossSell
      ? banners?.registrationV2?.crossSellOffer_1
      : banners?.registrationV2?.offer_1;

    const offer2 = availableCrossSell
      ? banners?.registrationV2?.crossSellOffer_2
      : banners?.registrationV2?.offer_2;

    return [banners?.registrationV2?.title, offer1, offer2];
  })();

  const [title, offer_1, offer_2] = useMemo(
    () => [
      customOffer?.registrationBanner.title || cmsTitle || '',
      (customOffer?.registrationBanner.offer_1 || cmsOffer1 || '').split('+'),
      customOffer?.registrationBanner.offer_1
        ? customOffer?.registrationBanner.offer_2
        : cmsOffer2 || '',
    ],
    [
      customOffer?.registrationBanner.title,
      customOffer?.registrationBanner.offer_1,
      customOffer?.registrationBanner.offer_2,
      cmsTitle,
      cmsOffer1,
      cmsOffer2,
    ]
  );

  const useStoreValues = regModal || isSports || regBannerStore?.isSportsPromo;
  const currentTitle = (useStoreValues && regBannerStore?.title) || title;
  const currentOffer1 = (useStoreValues && regBannerStore?.offer1) || offer_1;
  const currentOffer2 =
    useStoreValues && regBannerStore ? regBannerStore?.offer2 : offer_2;

  return (
    <>
      <h2>{currentTitle}</h2>
      <p className="offer-1">
        {currentOffer1?.[0]}{' '}
        {currentOffer1?.[1] && <span>+ {currentOffer1?.[1]}</span>}
      </p>
      {currentOffer2 && <p className="offer-2">{currentOffer2}</p>}
    </>
  );
};
